import React, { type ComponentProps, useMemo } from 'react';
import { Tabs } from 'expo-router';
import { Platform } from 'react-native';

import { theme, useResponsive } from '@sg/garnish';

import { NavbarV2 } from '../../components';
import { APP_BACKGROUND, useTabOptions } from '../../routing';

// ─────────────────────────────────────────────────────────────────────────────

export default function TabsLayout() {
  const { match } = useResponsive();

  const {
    accountTabOptions,
    homeTabOptions,
    menuTabOptions,
    giftTabOptions,
    loyaltyTabOptions,
    reorderTabOptions,
  } = useTabOptions();

  // ─────────────────────────────────────────────────────────────────────

  const screenOptions = useMemo<TabsScreenOptions>(
    () => ({
      headerShown: false,
      header: undefined,
      sceneContainerStyle: { backgroundColor: APP_BACKGROUND },
      tabBarPosition: match(['bottom', 'top']),
      tabBarStyle: {
        backgroundColor: APP_BACKGROUND,
        borderTopWidth: 1,
        borderTopColor: theme.colors.OPACITY.DARK_KALE.DARK,
        paddingTop: theme.spacing[2],
      },
      tabBarActiveTintColor: theme.colors.KALE,
      tabBarInactiveTintColor: theme.colors.OPACITY.BLACK.DARK,
    }),
    [match],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Tabs
      screenOptions={screenOptions}
      tabBar={renderTabBar}
      backBehavior={Platform.select({ web: 'history', default: 'firstRoute' })}
    >
      <Tabs.Screen name="(home)" options={homeTabOptions} />
      <Tabs.Screen name="(menu)" options={menuTabOptions} />
      <Tabs.Screen name="rewards" options={loyaltyTabOptions} />
      <Tabs.Screen name="reorder-tab" options={reorderTabOptions} />
      <Tabs.Screen name="gift" options={giftTabOptions} />

      {/* hide some tabs */}

      <Tabs.Screen name="account" options={accountTabOptions} />
      <Tabs.Screen name="menu" options={{ href: null }} />
    </Tabs>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function renderTabBar(props: Parameters<TabBarRenderer>[0]) {
  return <NavbarV2 tabBarProps={props} />;
}

// ─── Types ──────────────────────────────────────────────────────────────────

type TabsScreenOptions = NonNullable<ComponentProps<typeof Tabs>['screenOptions']>;

type TabBarRenderer = NonNullable<ComponentProps<typeof Tabs>['tabBar']>;
